import color from "@constants/color";
import React, { useState, useCallback, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Modal,
  Platform,
  UIManager,
} from "react-native";

if (Platform.OS === "android") {
  if (UIManager.setLayoutAnimationEnabledExperimental) {
    UIManager.setLayoutAnimationEnabledExperimental(true);
  }
}

const DROPDOWN_MAX_HEIGHT = 360;

const DROPDOWN_ITEM_HEIGHT = 40;

const DROPDOWN_BOTTOM_MARGIN = 12;

const DropdownButton = ({
  items,
  value,
  open,
  setOpen,
  buttonRef,
  setDropdownLayout,
  style,
  textStyle,
  schema,
  placeholder,
}) => {
  const selectedItem = items.find((item) => item[schema.value] === value);

  return (
    <TouchableOpacity
      activeOpacity={0.8}
      ref={buttonRef}
      style={[styles.dropdownButton, style]}
      onPress={() => {
        if (buttonRef.current) {
          buttonRef.current.measure((x, y, width, height, pageX, pageY) => {
            setDropdownLayout({ width, height, pageX, pageY });
            setOpen(!open);
          });
        }
      }}
    >
      <Text style={[styles.dropdownButtonText, textStyle]}>
        {selectedItem ? selectedItem[schema.label] : placeholder}
      </Text>
      <Text style={[styles.dropdownButtonText, textStyle]}>
        {open ? "▲" : "▼"}
      </Text>
    </TouchableOpacity>
  );
};

const CustomPicker = ({
  items = [],
  value,
  setValue,
  placeholder = "Select an item",
  style = {},
  textStyle = {},
  schema = { label: "label", value: "value" },
  open,
  setOpen,
}) => {
  const [dropdownLayout, setDropdownLayout] = useState(null);
  const [overlayHeight, setOverlayHeight] = useState(0);
  const buttonRef = useRef(null);

  const renderItem = useCallback(
    ({ item }) => (
      <TouchableOpacity
        style={styles.dropdownItem}
        onPress={() => {
          setValue(item[schema.value]);
          setOpen(false);
        }}
      >
        <Text style={[styles.dropdownItemText, textStyle]}>
          {item[schema.label]}
        </Text>
      </TouchableOpacity>
    ),
    [setValue, setOpen, schema, textStyle],
  );

  const dropdownTop = dropdownLayout
    ? dropdownLayout.pageY + dropdownLayout.height + 5
    : 0;
  const availableHeight = overlayHeight
    ? overlayHeight - dropdownTop - DROPDOWN_BOTTOM_MARGIN
    : DROPDOWN_MAX_HEIGHT;
  const dropdownMaxHeight = Math.min(
    DROPDOWN_MAX_HEIGHT,
    Math.max(DROPDOWN_ITEM_HEIGHT, availableHeight),
  );

  return (
    <View style={styles.container}>
      <DropdownButton
        items={items}
        value={value}
        open={open}
        setOpen={setOpen}
        buttonRef={buttonRef}
        setDropdownLayout={setDropdownLayout}
        style={style}
        textStyle={textStyle}
        schema={schema}
        placeholder={placeholder}
      />
      <Modal
        statusBarTranslucent
        navigationBarTranslucent
        visible={open}
        transparent
        animationType="none"
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={0.8}
          onPress={() => setOpen(false)}
          onLayout={(e) => setOverlayHeight(e.nativeEvent.layout.height)}
        >
          {dropdownLayout && (
            <View
              style={[
                styles.dropdownListContainer,
                styles.dropdownPosition,
                {
                  top: dropdownTop,
                  left: dropdownLayout.pageX,
                  width: dropdownLayout.width,
                  maxHeight: dropdownMaxHeight,
                },
              ]}
            >
              <FlatList
                data={items}
                renderItem={renderItem}
                keyExtractor={(item) => item[schema.value].toString()}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
                scrollEnabled={true}
                style={{ maxHeight: dropdownMaxHeight }}
                contentContainerStyle={styles.flatListContent}
                keyboardShouldPersistTaps="handled"
                initialNumToRender={8}
                windowSize={10}
                maxToRenderPerBatch={10}
              />
            </View>
          )}
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "relative",
    zIndex: 1000,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: color.lighterBlack,
  },
  dropdownButton: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: color.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: color.blank_grey,
  },
  dropdownButtonText: {
    fontSize: 16,
    color: color.mediumBlack,
  },
  dropdownListContainer: {
    backgroundColor: color.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: color.blank_grey,
    borderTopWidth: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    overflow: "hidden",
  },
  dropdownPosition: {
    position: "absolute",
  },
  flatListContent: {
    flexGrow: 0,
  },
  dropdownItem: {
    paddingHorizontal: 12,
    justifyContent: "center",
    borderBottomWidth: 1,
    borderBottomColor: color.lightGrey,
    height: DROPDOWN_ITEM_HEIGHT,
  },
  dropdownItemText: {
    fontSize: 16,
    color: color.mediumBlack,
  },
});

export default CustomPicker;
